import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence } from 'motion/react';
import { useCalendarGridStore } from '../../../store/calendar-grid.store';
import { CalendarEvent } from '../../../types';
import { EventRenderer } from '../event-renderer';
import { OverflowCountButton } from './overflow-count-button';
import {
  checkIsGroupableEventType,
  getOverlappingEventGroups,
  getVisibleOverlapLaneCount,
  OVERLAP_EVENT_MIN_CARD_WIDTH,
  OVERLAP_EVENTS_BUTTON_WIDTH,
} from './utils';

interface Props {
  eventIds: string[];
}

export const OverlappingEvents = memo(({ eventIds }: Props) => {
  const { eventsById, groupColumnWidth } = useCalendarGridStore(['eventsById', 'groupColumnWidth']);
  const events = eventIds.map((eventId) => eventsById[eventId]).filter((event): event is CalendarEvent => !!event);

  const groupableEvents = events.filter((event) => checkIsGroupableEventType(event.eventType));
  const nonGroupableEvents = events.filter((event) => !checkIsGroupableEventType(event.eventType));
  const overlapGroups = useMemo(() => getOverlappingEventGroups(groupableEvents), [groupableEvents]);
  const singleLaneEvents = useMemo(
    () => overlapGroups.filter((group) => group.laneCount === 1).flatMap((group) => group.lanes[0]?.events ?? []),
    [overlapGroups]
  );
  const multiLaneGroups = useMemo(() => overlapGroups.filter((group) => group.laneCount > 1), [overlapGroups]);
  const groupLayouts = useMemo(
    () =>
      multiLaneGroups.map((group) => {
        const visibleLaneCount = getVisibleOverlapLaneCount(groupColumnWidth, group.laneCount);
        const hasOverflow = group.laneCount > visibleLaneCount;

        return {
          ...group,
          visibleLaneCount,
          hasOverflow,
        };
      }),
    [groupColumnWidth, multiLaneGroups]
  );

  return (
    <div css={columnEventsLayerStyle}>
      {nonGroupableEvents.map((event) => (
        <EventRenderer key={event.id} eventId={event.id} style={{ gridColumn: '1 / -1' }} />
      ))}
      {singleLaneEvents.map((event) => (
        <EventRenderer key={event.id} eventId={event.id} style={{ gridColumn: '1 / -1' }} />
      ))}
      {groupLayouts.map((group, groupIndex) => {
        const visibleLanes = group.lanes.slice(0, group.visibleLaneCount);
        const hiddenEvents = group.lanes.slice(group.visibleLaneCount).flatMap((lane) => lane.events);
        const firstHiddenEvent = hiddenEvents[0];

        return (
          <div
            key={`overlap-group-${groupIndex}`}
            css={overlapGroupLayerStyle}
            style={{
              gridTemplateColumns: `${Array.from(
                { length: group.visibleLaneCount },
                () => `minmax(${OVERLAP_EVENT_MIN_CARD_WIDTH}px, 1fr)`
              ).join(' ')}${group.hasOverflow ? ` ${OVERLAP_EVENTS_BUTTON_WIDTH}px` : ''}`,
            }}
          >
            {visibleLanes.map((lane, laneIndex) =>
              lane.events.map((event) => (
                <EventRenderer key={event.id} eventId={event.id} style={{ gridColumn: laneIndex + 1 }} />
              ))
            )}
            <AnimatePresence>
              {group.hasOverflow && firstHiddenEvent && (
                <OverflowCountButton
                  style={{
                    gridColumn: group.visibleLaneCount + 1,
                    gridRow: `${firstHiddenEvent.startRow} / span 1`,
                  }}
                  hiddenEventIds={hiddenEvents.map((event) => event.id)}
                />
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
});
OverlappingEvents.displayName = 'OverlappingEvents';

const columnEventsLayerStyle = css({
  position: 'absolute',
  inset: 0,
  width: '100%',
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'repeat(var(--total-mins), var(--row-height))',
  gridAutoFlow: 'row',
});

const overlapGroupLayerStyle = css({
  position: 'absolute',
  inset: 0,
  width: '100%',
  minWidth: 0,
  display: 'grid',
  gridTemplateRows: 'repeat(var(--total-mins), var(--row-height))',
  gridAutoFlow: 'row',
});
